import api from '@/lib/axios';
import {
  Site,
  SitePath,
  SiteResource,
  SiteResourceIdParams,
  SiteResourceListParams,
  SiteResourceListResponse,
  SiteResolvedResource,
  SiteResourceResolveByCodesParams,
  SiteResourceSaveParams,
  SiteResourceSubtypeCountParams,
  SiteResourceSubtypeCountResponse,
  SiteResourceUploadCompleteParams,
  SiteResourceUploadInitParams,
  SiteResourceUploadInitResponse,
  CreateSiteCampaignBlueprintParams,
  CreateSiteParams,
  UpdateSiteParams,
  UpdateSiteCampaignBlueprintParams,
  BindDomainParams,
  SitePageParams,
  SitePageResponse,
  SiteCampaignBlueprint,
  SiteCampaignBlueprintIdParams,
  SiteCampaignBlueprintListParams,
  SiteCampaignBlueprintListResponse,
  SitePathListParams,
  DeleteSitePathParams,
  DeleteSiteParams,
  DnsGuideParams,
  DnsConfigGuide,
  SiteDomainSetupInfoParams,
  SiteDomainSetupInfo,
  DomainStatusRequest,
  DomainStatusResponse,
} from '@/types/site';

/**
 * 站点模块 API
 * 封装站点创建、更新、列表查询及域名绑定等功能
 */
export const siteApi = {
  /**
   * 创建新站点
   */
  createSite: (data: CreateSiteParams): Promise<Site> => {
    return api.post('/lowcode-business/api/user/site/create', data);
  },

  /**
   * 更新站点信息
   */
  updateSite: (data: UpdateSiteParams): Promise<Site> => {
    return api.post('/lowcode-business/api/user/site/update', data);
  },

  /**
   * 分页查询站点列表
   */
  getSiteList: (params: SitePageParams): Promise<SitePageResponse> => {
    return api.post('/lowcode-business/api/user/site/findListByPage', params);
  },

  /**
   * 绑定自定义域名
   */
  bindDomain: (data: BindDomainParams): Promise<Site> => {
    return api.post('/lowcode-business/api/user/site/bindDomain', data);
  },

  /**
   * 获取站点路径列表
   */
  getSitePathList: (params: SitePathListParams): Promise<SitePath[]> => {
    return api.post('/lowcode-business/api/user/site/path/list', params);
  },

  /**
   * 删除站点路径
   */
  deleteSitePath: (data: DeleteSitePathParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/site/path/delete', data);
  },

  /**
   * 删除站点（软删除，同时级联删除所有路径关联）
   * POST /lowcode-business/api/user/site/delete
   */
  deleteSite: (data: DeleteSiteParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/site/delete', data);
  },

  /**
   * 触发域名认证（申请 SSL 证书）
   * 要求站点 status=1（待认证）
   * POST /lowcode-business/api/user/site/auth
   */
  authSite: (id: number): Promise<Site> => {
    return api.post('/lowcode-business/api/user/site/auth', { id }, { _suppressErrorToast: true });
  },

  /**
   * 解绑自定义域名
   * POST /lowcode-business/api/user/site/unbindDomain
   */
  unbindDomain: (id: number): Promise<Site> => {
    return api.post('/lowcode-business/api/user/site/unbindDomain', { id });
  },

  /**
   * 查询 DNS 配置指引列表
   * 根据项目类型和域名类型返回所有启用的 DNS 配置指引列表
   */
  getDnsGuideList: (data: DnsGuideParams): Promise<DnsConfigGuide[]> => {
    return api.post('/lowcode-business/api/user/site/dns-guide/list', data);
  },

  getDomainSetupInfo: (data: SiteDomainSetupInfoParams): Promise<SiteDomainSetupInfo> => {
    return api.post('/lowcode-business/api/user/site/domain-setup-info', data, {
      _suppressErrorToast: true,
    });
  },

  /**
   * 检查域名注册状态和 DNS 记录状态
   * POST /api/user/domainname/status/check
   */
  checkDomainStatus: (data: DomainStatusRequest): Promise<DomainStatusResponse> => {
    return api.post('/lowcode-business/api/user/domainname/status/check', data, {
      _suppressErrorToast: true,
    });
  },

  getResourceList: (params: SiteResourceListParams): Promise<SiteResourceListResponse> => {
    return api.post('/lowcode-business/api/user/site/resource/list', params);
  },

  getResourceSubtypeCount: (
    params: SiteResourceSubtypeCountParams,
  ): Promise<SiteResourceSubtypeCountResponse> => {
    return api.post('/lowcode-business/api/user/site/resource/subtype/count', params);
  },

  getResourceDetail: (data: SiteResourceIdParams): Promise<SiteResource> => {
    return api.post('/lowcode-business/api/user/site/resource/detail', data);
  },

  resolveResourcesByCodes: (
    data: SiteResourceResolveByCodesParams,
  ): Promise<SiteResolvedResource[]> => {
    return api.post('/lowcode-business/api/user/site/resource/resolveByCodes', data);
  },

  saveResource: (data: SiteResourceSaveParams): Promise<SiteResource> => {
    return api.post('/lowcode-business/api/user/site/resource/save', data);
  },

  deleteResource: (data: SiteResourceIdParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/site/resource/delete', data);
  },

  initResourceUpload: (
    data: SiteResourceUploadInitParams,
  ): Promise<SiteResourceUploadInitResponse> => {
    return api.post('/lowcode-business/api/user/site/resource/upload/init', data);
  },

  completeResourceUpload: (data: SiteResourceUploadCompleteParams): Promise<SiteResource> => {
    return api.post('/lowcode-business/api/user/site/resource/upload/complete', data);
  },

  getCampaignBlueprintList: (
    data: SiteCampaignBlueprintListParams,
  ): Promise<SiteCampaignBlueprintListResponse> => {
    return api.post('/lowcode-business/api/user/site/campaign-blueprint/list', data);
  },

  getCampaignBlueprintDetail: (
    data: SiteCampaignBlueprintIdParams,
  ): Promise<SiteCampaignBlueprint> => {
    return api.post('/lowcode-business/api/user/site/campaign-blueprint/detail', data);
  },

  createCampaignBlueprint: (
    data: CreateSiteCampaignBlueprintParams,
  ): Promise<SiteCampaignBlueprint> => {
    return api.post('/lowcode-business/api/user/site/campaign-blueprint/create', data);
  },

  updateCampaignBlueprint: (
    data: UpdateSiteCampaignBlueprintParams,
  ): Promise<SiteCampaignBlueprint> => {
    return api.post('/lowcode-business/api/user/site/campaign-blueprint/update', data);
  },

  deleteCampaignBlueprint: (data: SiteCampaignBlueprintIdParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/site/campaign-blueprint/delete', data);
  },
};
